import { checkRoleWithOptions } from '@/helpers/roles'
import { useBillingStore } from '@/store/stores/billing'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import { CONNECTOR_TYPES, PROVIDER_TYPES } from '@/vars/general'
import config from '@root/config'

const GCB2_CHAT_MODULE_ID = String(config.marketplaceModulesIds[config.buildType].dhChat)

export interface Gcb2ChatProjectLike {
    id?: string
    billingEnabled?: boolean
    isDhChatEnabled?: boolean
    connectors?: Array<{
        deleted?: boolean
        providerType?: string
        connectorType?: string
        chatSettings?: { popupNotifications?: boolean }
    }>
}

export function hasMaxBusinessConnector(project?: Gcb2ChatProjectLike | null): boolean {
    return Boolean(
        project?.connectors?.some(
            (connector) => !connector.deleted && connector.providerType === PROVIDER_TYPES.maxBusiness
        )
    )
}

export function hasGcb2ChatTariffAccess(project?: Gcb2ChatProjectLike | null): boolean {
    if (!project?.id || !project.billingEnabled) {
        return false
    }
    const billingStore = useBillingStore()
    const currentTariff = billingStore.getCurrentTariff(project.id) as
        | { extra?: { includesDhChat?: boolean } | null }
        | undefined
    return Boolean(currentTariff?.extra?.includesDhChat)
}

export function hasGcb2ChatModuleAccess(project?: Gcb2ChatProjectLike | null): boolean {
    if (!project?.id) {
        return false
    }

    const marketplaceStore = useMarketplaceStore()
    const integration = marketplaceStore
        .allIntgrations(project.id)
        .find(item => String(item.id) === GCB2_CHAT_MODULE_ID)

    return integration?.status === 'installed'
}

export function hasGcb2ChatConnector(project?: Gcb2ChatProjectLike | null): boolean {
    // Backend sets isDhChatEnabled when entitled: Smart/Pro tariff, Multichat module,
    // MAX Business, or active Multichat / MAX Business promo.
    return Boolean(project?.isDhChatEnabled)
}

export function hasGcb2ChatViewAccess(project?: Gcb2ChatProjectLike | null): boolean {
    if (!project?.id || !project.billingEnabled) {
        return false
    }
    return checkRoleWithOptions(project.id, 'gcb2:chat:view', { ignoreIsActive: true })
}

export function hasGcb2ChatEditAccess(project?: Gcb2ChatProjectLike | null): boolean {
    if (!project?.id || !project.billingEnabled) {
        return false
    }
    return checkRoleWithOptions(project.id, 'gcb2:chat:edit', { ignoreIsActive: true })
}

export function isGcb2ChatPopupEnabled(project?: Gcb2ChatProjectLike | null): boolean {
    const gcb2Connector = project?.connectors?.find(
        (connector) => connector.connectorType === CONNECTOR_TYPES.GCB2
    )
    return gcb2Connector?.chatSettings?.popupNotifications !== false
}

export function isGcb2ChatOpenInRoute(
    route: { path?: string; query?: Record<string, unknown> },
    chatId: string
): boolean {
    if (!route.path?.includes('/GCB2')) return false
    if (route.query?.section !== 'chat') return false
    const rawChatId = route.query?.chatId
    const routeChatId = Array.isArray(rawChatId) ? rawChatId[0] : rawChatId
    return routeChatId != null && String(routeChatId) === String(chatId)
}
